/**
 * Holds an entire-screen MediaStream from system check until the secure
 * assessment lock adopts it at interview start.
 */

let pendingStream: MediaStream | null = null;

function isEntireDisplayShare(track: MediaStreamTrack | undefined): boolean {
  if (!track) return false;
  const settings = track.getSettings?.() || {};
  const displaySurface = String((settings as { displaySurface?: string }).displaySurface || '').toLowerCase();
  if (displaySurface) {
    return displaySurface === 'monitor';
  }
  // Safari/Firefox may omit displaySurface — accept with a soft pass.
  return true;
}

export function isEntireScreenShareTrack(track: MediaStreamTrack | undefined): boolean {
  return isEntireDisplayShare(track);
}

export function storeScreenShareHandoff(stream: MediaStream): boolean {
  const track = stream.getVideoTracks()[0];
  if (!isEntireDisplayShare(track)) {
    stream.getTracks().forEach((item) => item.stop());
    return false;
  }

  if (pendingStream && pendingStream !== stream) {
    pendingStream.getTracks().forEach((item) => item.stop());
  }

  pendingStream = stream;
  track.addEventListener('ended', () => {
    if (pendingStream === stream) {
      pendingStream = null;
    }
  });

  return true;
}

export function peekScreenShareHandoff(): MediaStream | null {
  const stream = pendingStream;
  if (!stream) return null;
  const track = stream.getVideoTracks()[0];
  if (!track || track.readyState === 'ended') {
    pendingStream = null;
    return null;
  }
  return stream;
}

/** Take ownership of the handoff stream (caller must stop tracks on cleanup). */
export function takeScreenShareHandoff(): MediaStream | null {
  const stream = peekScreenShareHandoff();
  pendingStream = null;
  return stream;
}

export function clearScreenShareHandoff(stopTracks = true): void {
  if (pendingStream && stopTracks) {
    pendingStream.getTracks().forEach((track) => track.stop());
  }
  pendingStream = null;
}

export async function requestEntireScreenShare(): Promise<{ ok: true; stream: MediaStream } | { ok: false; error: string }> {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'monitor',
        frameRate: { ideal: 5, max: 10 },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } as MediaTrackConstraints,
      audio: false
    });

    if (!storeScreenShareHandoff(stream)) {
      return {
        ok: false,
        error: 'Please share your entire screen (not a single window or tab).'
      };
    }

    return { ok: true, stream };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Screen share permission was denied'
    };
  }
}
